import { API_ENDPOINT } from "@/constants/appConstants";
import { useMutation } from "@tanstack/react-query";

import { LoginErrorResponse } from "@/components/login/types";

import {
  CheckExistenceParam,
  LoginUserParam,
  SendOtpParam,
  verifyOtpParam,
} from "./type";

// API call to check if user exists (by email or number)
const checkUserExistence = async (params: CheckExistenceParam) => {
  const url = API_ENDPOINT(window.location.host).middleware[
    params.isEmail ? "checkEmailExistence" : "checkNumberExistence"
  ];
  const response = await fetch(url, {
    method: "POST",
    body: JSON.stringify({ payload: params.payload }),
  });

  if (!response.ok) {
    const errorData = (await response.json()) as LoginErrorResponse;
    throw new Error(errorData.error.message.message || "Something went wrong");
  }
  return response.json();
};

// React Query mutation hook for checking user existence
export const useCheckUserExistence = () => {
  return useMutation({
    mutationFn: checkUserExistence,
  });
};

// API call for sending OTP
const sendOtpRequest = async (params: SendOtpParam) => {
  const url = API_ENDPOINT(window.location.host).middleware.getMobileOTPV4;
  const response = await fetch(url, {
    method: "POST",
    body: JSON.stringify(params),
  });
  if (!response.ok) {
    const errorData = (await response.json()) as LoginErrorResponse;
    throw new Error(errorData.error.message.message || "Something went wrong");
  }
  return response.json();
};

// React Query mutation hook for sending OTP
export const useSendOTP = () => {
  return useMutation({
    mutationFn: sendOtpRequest,
  });
};

// API call for verify OTP
const verifyOtpRequest = async (params: verifyOtpParam) => {
  const url = API_ENDPOINT(window.location.host).middleware.anonymousVerifyOtp;
  const response = await fetch(url, {
    method: "POST",
    body: JSON.stringify(params),
  });
  if (!response.ok) {
    const errorData = (await response.json()) as LoginErrorResponse;
    throw new Error(errorData.error.message.message || "Something went wrong");
  }
  return response.json();
};

// React Query mutation hook for sending OTP
export const useVerifyOTP = () => {
  return useMutation({
    mutationFn: verifyOtpRequest,
  });
};

const loginUserRequest = async (
  params: LoginUserParam & { queryParams?: URLSearchParams },
) => {
  const { queryParams, ...credentials } = params;

  let url = API_ENDPOINT(window.location.host).middleware.login;
  if (queryParams && queryParams.toString()) {
    url = `${url}?${queryParams.toString()}`;
  }

  const response = await fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    credentials: "include", // Important: sends cookies
    body: JSON.stringify(credentials),
  });

  if (!response.ok) {
    const errorData = (await response.json()) as LoginErrorResponse;
    throw new Error(errorData.error.message.message || "Something went wrong");
  }
  return response.json();
};

// React Query mutation hook for login with redirect
export const useLoginUser = () => {
  return useMutation({
    mutationFn: loginUserRequest,
  });
};
